import { useEffect, useRef, type RefObject } from 'react';
import { selectImageAction } from '../actions/selectImageAction';

interface UseSelectedImageSyncParams {
  activeSelectedId: string | undefined;
  selectedImageId: string | undefined;
  imageRefs: RefObject<Map<string, HTMLElement>>;
}

export function useSelectedImageSync({
  activeSelectedId,
  selectedImageId,
  imageRefs
}: UseSelectedImageSyncParams): void {
  const isFirstScrollRef = useRef(true);

  useEffect(() => {
    if (!activeSelectedId) return;

    if (activeSelectedId !== selectedImageId) {
      selectImageAction({ imageId: activeSelectedId });
    }

    const scroll = () => {
      imageRefs.current?.get(activeSelectedId)?.scrollIntoView({
        behavior: isFirstScrollRef.current ? 'instant' : 'smooth',
        inline: 'start',
        block: 'nearest'
      });
      isFirstScrollRef.current = false;
    };

    if (isFirstScrollRef.current) {
      if (document.readyState === 'complete') {
        const id = requestAnimationFrame(scroll);

        return () => cancelAnimationFrame(id);
      }

      let rafId: number;

      const onLoad = () => {
        rafId = requestAnimationFrame(scroll);
      };
      window.addEventListener('load', onLoad, { once: true });

      return () => {
        window.removeEventListener('load', onLoad);
        cancelAnimationFrame(rafId);
      };
    }

    return scroll();
  }, [activeSelectedId, selectedImageId, imageRefs]);
}
